import { useState, useEffect } from 'react'
import {
  Settings as SettingsIcon,
  RefreshCw,
  CheckCircle,
  XCircle,
  Upload,
  FileSpreadsheet,
  Users,
  Plus,
  Edit2,
  Trash2,
  Shield,
  ShieldCheck,
  Eye,
  UserCog,
  Loader2,
  X,
  Save,
  Lock,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react'
import {
  getSyncStatus,
  syncAllTenants,
  importExcel,
  getAdminUsers,
  createAdminUser,
  updateAdminUser,
  deleteAdminUser,
  adminResetPassword,
  AdminUser,
} from '../lib/api'

interface SyncStatus {
  unifi_access_configured: boolean
  unifi_protect_configured: boolean
  tenants: {
    total: number
    synced: number
    pending: number
    errors: number
  }
  access_assignments: {
    total: number
    synced: number
  }
}

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin', icon: ShieldCheck, color: 'text-red-600 bg-red-50' },
  { value: 'manager', label: 'Manager', icon: Shield, color: 'text-blue-600 bg-blue-50' },
  { value: 'staff', label: 'Staff', icon: UserCog, color: 'text-green-600 bg-green-50' },
  { value: 'viewer', label: 'Viewer', icon: Eye, color: 'text-gray-600 bg-gray-50' },
]

function getRoleBadge(role: string) {
  const opt = ROLE_OPTIONS.find((r) => r.value === role)
  if (!opt) {
    // Handle super_admin or unknown roles
    if (role === 'super_admin') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-purple-700 bg-purple-50">
          <ShieldCheck className="w-3 h-3" />
          Super Admin
        </span>
      )
    }
    return <span className="text-xs text-gray-500">{role}</span>
  }
  const Icon = opt.icon
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${opt.color}`}>
      <Icon className="w-3 h-3" />
      {opt.label}
    </span>
  )
}

// ─── Add/Edit User Modal ────────────────────────────────────────────────

interface UserFormData {
  email: string
  first_name: string
  last_name: string
  password: string
  role: string
}

function UserModal({
  editUser,
  onClose,
  onSaved,
}: {
  editUser: AdminUser | null
  onClose: () => void
  onSaved: () => void
}) {
  const isEdit = editUser !== null
  const [form, setForm] = useState<UserFormData>({
    email: editUser?.email || '',
    first_name: editUser?.first_name || '',
    last_name: editUser?.last_name || '',
    password: '',
    role: editUser?.role || 'admin',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!form.email || !form.first_name || !form.last_name) {
      setError('Email, first name, and last name are required.')
      return
    }
    if (!isEdit && !form.password) {
      setError('Password is required for new users.')
      return
    }
    if (!isEdit && form.password.length < 6) {
      setError('Password must be at least 6 characters.')
      return
    }

    setSaving(true)
    try {
      if (isEdit) {
        await updateAdminUser(editUser!.id, {
          first_name: form.first_name,
          last_name: form.last_name,
          role: form.role,
        })
      } else {
        await createAdminUser({
          email: form.email,
          first_name: form.first_name,
          last_name: form.last_name,
          password: form.password,
          role: form.role,
        })
      }
      onSaved()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Operation failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            {isEdit ? 'Edit User' : 'Add Admin User'}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              disabled={isEdit}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
              placeholder="user@example.com"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              <input
                type="text"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input
                type="text"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {!isEdit && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Min 6 characters"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Reset Password Modal ───────────────────────────────────────────────

function ResetPasswordModal({
  user,
  onClose,
}: {
  user: AdminUser
  onClose: () => void
}) {
  const [newPassword, setNewPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  const handleReset = async () => {
    setError('')
    setMessage('')
    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters.')
      return
    }
    if (newPassword !== confirm) {
      setError('Passwords do not match.')
      return
    }
    setSaving(true)
    try {
      await adminResetPassword(user.email, newPassword)
      setMessage(`Password reset for ${user.email}`)
      setTimeout(onClose, 1500)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Reset failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Reset Password</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            Reset password for <strong>{user.email}</strong>
          </p>

          {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}
          {message && <div className="p-3 bg-green-50 text-green-700 rounded-lg text-sm">{message}</div>}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleReset}
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              {saving ? 'Resetting...' : 'Reset'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Main Settings Page ─────────────────────────────────────────────────

export default function Settings() {
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState<Record<string, unknown> | null>(null)

  // Admin user management state
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([])
  const [loadingUsers, setLoadingUsers] = useState(true)
  const [showUserModal, setShowUserModal] = useState(false)
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null)
  const [resetPwUser, setResetPwUser] = useState<AdminUser | null>(null)
  const [togglingId, setTogglingId] = useState<number | null>(null)

  const fetchSyncStatus = async () => {
    try {
      const data = await getSyncStatus() as SyncStatus
      setSyncStatus(data)
    } catch (err) {
      console.error('Failed to load sync status:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchAdminUsers = async () => {
    setLoadingUsers(true)
    try {
      const users = await getAdminUsers()
      setAdminUsers(users)
    } catch (err) {
      console.error('Failed to load admin users:', err)
    } finally {
      setLoadingUsers(false)
    }
  }

  useEffect(() => {
    fetchSyncStatus()
    fetchAdminUsers()
  }, [])

  const handleSyncAll = async () => {
    setSyncing(true)
    try {
      await syncAllTenants()
      await fetchSyncStatus()
    } catch (err) {
      alert('Sync failed')
    } finally {
      setSyncing(false)
    }
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setImporting(true)
    setImportResult(null)
    try {
      const result = await importExcel(file) as Record<string, unknown>
      setImportResult(result)
    } catch (err) {
      alert('Import failed')
    } finally {
      setImporting(false)
    }
  }

  const handleToggleActive = async (user: AdminUser) => {
    if (user.role === 'super_admin') return
    setTogglingId(user.id)
    try {
      await updateAdminUser(user.id, { is_active: !user.is_active })
      await fetchAdminUsers()
    } catch (err) {
      alert('Failed to update user status')
    } finally {
      setTogglingId(null)
    }
  }

  const handleDeleteUser = async (user: AdminUser) => {
    if (user.role === 'super_admin') return
    if (!confirm(`Deactivate ${user.email}? They will no longer be able to log in.`)) return
    try {
      await deleteAdminUser(user.id)
      await fetchAdminUsers()
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Delete failed')
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">Configuration, data management, and user administration</p>
      </div>

      {/* ─── Admin User Management ──────────────────────────── */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 rounded-lg">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Admin Users</h2>
              <p className="text-sm text-gray-500">Manage who has access to this admin portal</p>
            </div>
          </div>
          <button
            onClick={() => {
              setEditingUser(null)
              setShowUserModal(true)
            }}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add User
          </button>
        </div>

        {loadingUsers ? (
          <div className="flex items-center justify-center h-24">
            <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
          </div>
        ) : adminUsers.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No admin users found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-gray-500">
                  <th className="pb-3 font-medium">User</th>
                  <th className="pb-3 font-medium">Email</th>
                  <th className="pb-3 font-medium">Role</th>
                  <th className="pb-3 font-medium">Status</th>
                  <th className="pb-3 font-medium">Last Login</th>
                  <th className="pb-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {adminUsers.map((user) => {
                  const isSuperAdmin = user.role === 'super_admin'
                  return (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-primary-700 font-semibold text-xs">
                            {(user.first_name?.[0] || '').toUpperCase()}
                            {(user.last_name?.[0] || '').toUpperCase()}
                          </div>
                          <span className="font-medium text-gray-900">
                            {user.first_name} {user.last_name}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 text-gray-600">{user.email}</td>
                      <td className="py-3">{getRoleBadge(user.role)}</td>
                      <td className="py-3">
                        <button
                          onClick={() => handleToggleActive(user)}
                          disabled={isSuperAdmin || togglingId === user.id}
                          className="flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                          title={isSuperAdmin ? 'Cannot modify super admin' : `Click to ${user.is_active ? 'deactivate' : 'activate'}`}
                        >
                          {togglingId === user.id ? (
                            <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                          ) : user.is_active ? (
                            <ToggleRight className="w-5 h-5 text-green-500" />
                          ) : (
                            <ToggleLeft className="w-5 h-5 text-gray-400" />
                          )}
                          <span className={`text-xs ${user.is_active ? 'text-green-600' : 'text-gray-400'}`}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </button>
                      </td>
                      <td className="py-3 text-gray-500 text-xs">
                        {user.last_login
                          ? new Date(user.last_login).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                            })
                          : 'Never'}
                      </td>
                      <td className="py-3">
                        <div className="flex items-center justify-end gap-1">
                          {!isSuperAdmin && (
                            <>
                              <button
                                onClick={() => {
                                  setEditingUser(user)
                                  setShowUserModal(true)
                                }}
                                className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-700"
                                title="Edit user"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setResetPwUser(user)}
                                className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-700"
                                title="Reset password"
                              >
                                <Lock className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteUser(user)}
                                className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-600"
                                title="Deactivate user"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          {isSuperAdmin && (
                            <span className="text-xs text-gray-400 italic">Protected</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── Existing Cards ─────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* UniFi Sync Status */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-primary-50 rounded-lg">
              <RefreshCw className="w-6 h-6 text-primary-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">UniFi Sync Status</h2>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Integration Status */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">UniFi Access</span>
                {syncStatus?.unifi_access_configured ? (
                  <span className="flex items-center gap-1 text-green-600 text-sm">
                    <CheckCircle className="w-4 h-4" />
                    Configured
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-red-600 text-sm">
                    <XCircle className="w-4 h-4" />
                    Not Configured
                  </span>
                )}
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">UniFi Protect</span>
                {syncStatus?.unifi_protect_configured ? (
                  <span className="flex items-center gap-1 text-green-600 text-sm">
                    <CheckCircle className="w-4 h-4" />
                    Configured
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-red-600 text-sm">
                    <XCircle className="w-4 h-4" />
                    Not Configured
                  </span>
                )}
              </div>

              {/* Resident Sync Stats */}
              <div className="mt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Resident Sync</h3>
                <div className="grid grid-cols-4 gap-2">
                  <div className="text-center p-2 bg-gray-100 rounded">
                    <p className="text-lg font-bold text-gray-900">{syncStatus?.tenants.total || 0}</p>
                    <p className="text-xs text-gray-500">Total</p>
                  </div>
                  <div className="text-center p-2 bg-green-50 rounded">
                    <p className="text-lg font-bold text-green-600">{syncStatus?.tenants.synced || 0}</p>
                    <p className="text-xs text-gray-500">Synced</p>
                  </div>
                  <div className="text-center p-2 bg-yellow-50 rounded">
                    <p className="text-lg font-bold text-yellow-600">{syncStatus?.tenants.pending || 0}</p>
                    <p className="text-xs text-gray-500">Pending</p>
                  </div>
                  <div className="text-center p-2 bg-red-50 rounded">
                    <p className="text-lg font-bold text-red-600">{syncStatus?.tenants.errors || 0}</p>
                    <p className="text-xs text-gray-500">Errors</p>
                  </div>
                </div>
              </div>

              <button
                onClick={handleSyncAll}
                disabled={syncing || !syncStatus?.unifi_access_configured}
                className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Sync All Residents'}
              </button>
            </div>
          )}
        </div>

        {/* Excel Import */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-green-50 rounded-lg">
              <FileSpreadsheet className="w-6 h-6 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Excel Import</h2>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            Import tenants, units, and vehicles from an Excel spreadsheet.
          </p>

          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              type="file"
              id="excel-upload"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileUpload}
              className="hidden"
            />
            <label
              htmlFor="excel-upload"
              className="cursor-pointer"
            >
              <Upload className="w-10 h-10 mx-auto mb-3 text-gray-400" />
              <p className="text-sm text-gray-600">
                {importing ? 'Importing...' : 'Click to upload Excel file'}
              </p>
              <p className="text-xs text-gray-400 mt-1">.xlsx, .xls, or .csv</p>
            </label>
          </div>

          {importResult && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Import Results</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <p>Total Rows: {String(importResult.total_rows)}</p>
                <p>Buildings Created: {String(importResult.buildings_created)}</p>
                <p>Units Created: {String(importResult.units_created)}</p>
                <p>Tenants Created: {String(importResult.tenants_created)}</p>
                <p>Vehicles Created: {String(importResult.vehicles_created)}</p>
                {(importResult.errors as string[] || []).length > 0 && (
                  <div className="mt-2 text-red-600">
                    <p>Errors: {(importResult.errors as string[]).length}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-700">
              <strong>Required columns:</strong> building_name, unit_number, first_name, last_name
            </p>
            <p className="text-xs text-blue-600 mt-1">
              <strong>Optional:</strong> room, email, phone, lease_start, lease_end, license_plate, vehicle_make, vehicle_model
            </p>
          </div>
        </div>
      </div>

      {/* ─── Modals ─────────────────────────────────────────── */}
      {showUserModal && (
        <UserModal
          editUser={editingUser}
          onClose={() => {
            setShowUserModal(false)
            setEditingUser(null)
          }}
          onSaved={() => {
            setShowUserModal(false)
            setEditingUser(null)
            fetchAdminUsers()
          }}
        />
      )}
      {resetPwUser && (
        <ResetPasswordModal
          user={resetPwUser}
          onClose={() => setResetPwUser(null)}
        />
      )}
    </div>
  )
}
